import { useMemo } from "react";
import SeededRandomGenerator from "src/helpers/SeededRandomGenerator";
import { ParsedStickerRowItem } from "./types";

type GetParsedSticker = (args: {
	rowIndex: number;
	columnIndex: number;
}) => ParsedStickerRowItem | null;
const MaxAngle = 30;
const MinAngle = 15;
const EdgeShift = 0.25;
const useStickerRowItemAllocation = ({
	stickers,
	numColumns,
	padding,
}: {
	stickers: { url: string | null | undefined }[];
	numColumns: number;
	padding: number;
}) => {
	const getParsedSticker = useMemo<GetParsedSticker>(() => {
		if (stickers.length === 0) {
			const getParsedSticker: GetParsedSticker = () => null;
			return getParsedSticker;
		}
		const frequency = Math.max(0.8 - 0.05 * numColumns, 0.2);
		const getParsedSticker: GetParsedSticker = ({ rowIndex, columnIndex }) => {
			if (stickers.length === 0) return null;
			const i = numColumns * rowIndex + columnIndex;
			const gen = new SeededRandomGenerator(
				// Random manipulation of the seed to make the random number generator more unpredictable
				(i * 163_597) % 10_731,
			);
			const random = () => gen.random();
			const sticker = stickers[i % stickers.length];
			if (!sticker || !sticker.url) return null;
			// Ensures we can't get more than two in a row
			if (i % 3 === 0) return null;
			if (!(random() < frequency)) return null;
			const size = Math.floor((0.2 * random() + 0.8) * 130);
			const maybeAngle = (2 * random() - 1) * MaxAngle;
			// Shouldn't have more than 2 animated right next to each other
			const animated = random() < 0.4 && i % 2 == 0;
			const isLeftMost = columnIndex === 0;
			const isRightMost = columnIndex === numColumns - 1;
			const side = isRightMost && random() < 0.6 ? "right" : "left";
			const parsedSticker: ParsedStickerRowItem = {
				url: sticker.url,
				size,
				animated,
				horizontalShift:
					padding / 2 +
					(isLeftMost && side === "left"
						? -EdgeShift * size
						: side === "right"
							? -EdgeShift * size
							: 0.2 * (random() - 0.5) * size),
				verticalShift: padding / 2 + (rowIndex === 0 ? -0.4 * size : 0),
				rotation: animated
					? 0.2 * MaxAngle + random() * 0.3 * MaxAngle
					: Math.abs(maybeAngle) < MinAngle
						? 0
						: maybeAngle,
				animationDelay: animated ? Math.floor(random() * 2000) : 0,
				animationReverse: animated ? random() < 0.5 : false,
				// Only right-most column can safely right align without risk of collision
				side,
			};
			return parsedSticker;
		};
		return getParsedSticker;
	}, [numColumns, padding, stickers]);

	return {
		getParsedSticker,
	};
};
export default useStickerRowItemAllocation;
